const { loadScripts } = require('utils/LoadScripts');
const localeUtils = require('utils/LanguageLocale');
const Perf = require('utils/framework/Perf');
const userUtils = require('utils/User');
const Location = require('utils/Location.js');
const basketUtils = require('utils/Basket.js');
const buildServiceUrl = require('utils/Services').buildServiceUrl;
const certonaConstants = require('utils/certonaConstants');
const Events = require('utils/framework/Events');
const skuUtils = require('utils/Sku');
const PAGE_TYPES = certonaConstants.PAGE_TYPES;
const CERTONA_EVENTS = certonaConstants.CERTONA_EVENTS;
const Storage = require('utils/localStorage/Storage');
const LOCAL_STORAGE = require('utils/localStorage/Constants');
const { COMPONENT_NAMES } = require('utils/BCC');

/**
 * return pagetype string
 */

const getPageType = () => {
    let pagetype;
    if (Location.isBasketPage()) {
        pagetype = PAGE_TYPES.PICK_UP_CART; // UTS-1314 [CRM] [Certona] Display a BOPIS Store Inventory Certona Carousel in Basket
    } else if (Location.isHomePage()) {
        pagetype = PAGE_TYPES.HOME;
    } else if (Location.isProductPage()) {
        pagetype = PAGE_TYPES.PRODUCT;
    } else if (Location.isRootCategoryPage()) {
        pagetype = PAGE_TYPES.CATEGORY;
    } else if (Location.isMyProfilePage()) {
        pagetype = PAGE_TYPES.PROFILE;
    } else if (Location.isOrderConfirmationPage()) {
        pagetype = PAGE_TYPES.PURCHASE;
    } else if (Location.isContentStorePage()) {
        pagetype = Location.isHolidayPage() ? PAGE_TYPES.HOLIDAY : PAGE_TYPES.CONTENT;
    } else if (Location.isNoSearchResultsPage()) {
        pagetype = PAGE_TYPES.NO_SEARCH;
    } else if (Location.isCreditCardApplyPage()) {
        pagetype = PAGE_TYPES.LOYALTY;
    } else if (Location.isSearchPage()) {
        pagetype = PAGE_TYPES.SEARCH;
    } else if (Location.isCreditCardTabPage()) {
        pagetype = PAGE_TYPES.FPTAB;
    } else if (Location.isLovesListPage()) {
        pagetype = PAGE_TYPES.WISH_LIST;
    } else if (Location.isBrandNthCategoryPage()) {
        pagetype = PAGE_TYPES.BRANDS;
    } else if (Location.isNthCategoryPage()) {
        pagetype = PAGE_TYPES.NTH_CATEGORY;
    } else if (Location.isTargetedLandingPage()) {
        pagetype = PAGE_TYPES.LANDING_PROMO;
    } else if (Location.isPurchaseHistoryPage()) {
        pagetype = PAGE_TYPES.PURCHASE_HISTORY;
    }

    return pagetype;
};

const pageType = getPageType();

const shouldSetCustomerId = function () {
    return Location.isLovesListPage() ||
        Location.isMyProfilePage() ||
        Location.isBasketPage() ||
        Location.isOrderConfirmationPage() ||
        Location.isTargetedLandingPage() ||
        Location.isPurchaseHistoryPage();
};

//TODO: add isPurchaseHistoryPage() here to prevent recommendation data from
// being returned on initial page load
const pageHasRecommendations = function () {
    return !(Location.isSearchPage() || Location.isLovesListPage() ||
        Location.isBrandNthCategoryPage() || Location.isNthCategoryPage());
};

const getMarketingSegment = function () {
    const { AFFILIATE, EMAIL, PAID, ORGANIC } = certonaConstants.PDP_SEGMENTS;
    const SEGMENT_VALUES = {
        'aff': AFFILIATE,
        'ret': EMAIL,
        'tr': EMAIL,
        'ppc': PAID,
        'us_search': PAID,
        'ca_search': PAID,
        'google': PAID,
        'ytsrch': PAID,
        'gsp': PAID,
        'esv': PAID
    };
    const searchEngines = ['https://www.google.com/',
        'https://search.yahoo.com/', 'https://www.bing.com/'];
    const urlUtils = require('utils/Url');

    let queryParams = Location.getLocation().search;
    let marketingParam = urlUtils.getParamsByName('om_mmc', queryParams) || [];
    let marketingSegment = '';
    if (marketingParam.length) {

        // If om_mmc param exists in url, return its corresponding marketing segment/channel
        let segment = marketingParam[0].split('-', 1)[0];
        marketingSegment = SEGMENT_VALUES[segment];
    } else if (searchEngines.indexOf(urlUtils.getDocumentReferrer()) !== -1) {

        // Return organic search if no om_mmc param in url but the document referrer is
        // one of the 3 engines specified above
        marketingSegment = ORGANIC;
    }
    return marketingSegment;
};

// Returns a string containing the SkuId of hard good products in basket
// separated with a semi colon
const getSkuIdsInBasket = function (basketItems) {
    return basketItems.filter(li => skuUtils.isHardGood(li.sku)).
        map(li => li.sku.skuId).join(';');
};

const sendCertona = function (event, config = {}) {
    let store = require('Store');
    let categoryId = Location.isRootCategoryPage() && store.getState().category.currentCategory;
    let catPageType;
    if (Location.isNthCategoryPage()) {
        // Need to send correct page type if user switched between top and nth level cats
        catPageType = config.pageType ||
            store.getState().productRecs.certonaObject.certonaPageType;
    }
    window.dispatchEvent(new CustomEvent(event, {
        detail: Object.assign({
            pageType: catPageType || pageType,
            customerId: userUtils.getProfileId(),
            basketProducts: basketUtils.getAllBasketItems(),
            itemId: config.itemId,
            categoryId: categoryId
        }, config)
    }));
};

// This function will inject resonance.js dynamically. It should only be used on pages
// which don't include certona on page load (those in pageHasCertona() at Services.js)
// and which we can't antecipate that will or not have certona during page load.
//
// PS: at this moment we don't know if it works to load resonance.js both from <head>
// and later on dynamically (a page that needs certona both on page load + ProductFinderModal).
//
// So we might need to revisit this approach if that scenario ever comes up,
// because we don't even know at this point if certona would expect us to fire
// the event INIT_LOAD twice or once (calls are from initCertona() from Certona.js service
// during the page load and later on again from ProductFinderModal).
const injectCertona = function () {
    const script = buildServiceUrl.certona();

    loadScripts([script], () => {
        if (typeof Sephora !== 'undefined' &&
            Sephora.Util &&
            Sephora.Util.InflatorComps &&
            Sephora.Util.InflatorComps.services &&
            Sephora.Util.InflatorComps.services.loadEvents) {
            Sephora.Util.InflatorComps.services.loadEvents.CertonaLoaded = true;
        }
        Perf.report('Certona Loaded');

        initCertona();
    });
};

// This function assumes the page has already loaded resonance.js.
// Please double check if the page you are working on already have that script,
// otherwise you'll need to add your page either on <head> (by changing
// pageHasCertona() at Services.js) or calling injectCertona to load dynamically.
const initCertona = function () {
    // We'll have resonance.js send us the recommendation data when this event fires
    let store = require('Store');
    let watch = require('redux-watch');

    let basket = store.getState().basket;
    if (basket.isInitialized) {
        sendCertona(CERTONA_EVENTS.INIT_LOAD);
    } else {
        let unsubscribe = store.subscribe(
            watch(store.getState, 'basket')(newVal => {
                if (newVal.isInitialized) {
                    sendCertona(CERTONA_EVENTS.INIT_LOAD);
                    unsubscribe();
                }
            }), { ignoreAutoUnsubscribe: true }
        );
    }
};

const getApplicationId = function () {
    let appId;

    if (localeUtils.isUS()) {
        appId = Sephora.isDesktop() ? 'Sephora01' : 'Sephora03';
    } else {
        appId = Sephora.isDesktop() ? 'Sephora02' : 'Sephora04';
    }

    return appId;
};

const fireCertonaReady = function () {
    Sephora.Util.InflatorComps.services.loadEvents.CertonaReady = true;
    Perf.report('Certona Ready');
    window.dispatchEvent(new CustomEvent(Events.CertonaReady, { detail: {} }));
};

const fireCertonaFailed = function () {
    Sephora.Util.InflatorComps.services.loadEvents.CertonaFailed = true;
    Perf.report('Certona Failed');
    window.dispatchEvent(new CustomEvent(Events.CertonaFailed, { detail: {} }));
};

const getCertonaBccItems = function(items) {
    let certonaItems = [];
    for (let key in items) {
        if (items.hasOwnProperty(key)) {
            const item = items[key];
            item.certonaPageType &&
            item.componentType === COMPONENT_NAMES.SKU_GRID &&
            certonaItems.push(item);
        }
    }
    return certonaItems;
};

module.exports = {
    getPageType,
    injectCertona,
    initCertona,
    getApplicationId,
    fireCertonaReady,
    fireCertonaFailed,
    sendCertona,
    getMarketingSegment,
    getSkuIdsInBasket,
    getCertonaBccItems,
    shouldSetCustomerId,
    pageHasRecommendations
};
